import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { getToken } from '@optimus-web/common';
import { AssistantSuggestion, AssistantSuggestionType } from '@mews-commander/data-access';
import { useAssistantAutocompleteQuery } from '../../query/useAssistantAutocompleteQuery';
import { KeywordColor } from '../extentedTextInput/KeywordColor';

interface SuggestionsTheme {
    commandsColor: string;
    commandOperatorsColor: string;
    entitiesColor: string;
    parametersColor: string;
    operatorsColor: string;
    valuesColor: string;
    valuesBackgroundColor: string;
    textsColor: string;
    textsBackgroundColor: string;
}

export const useKeywordColors = (
    searchTerm: string,
    enterpriseIds: string[],
    assistantAutocompleteEnabled: boolean
): KeywordColor[] => {
    const { data: autocomplete } = useAssistantAutocompleteQuery(
        searchTerm,
        enterpriseIds ?? [],
        assistantAutocompleteEnabled
    );
    const suggestionsHistory = useMemo(() => autocomplete?.suggestionsHistory ?? [], [autocomplete]);

    const theme = useTheme();
    const commandsColor = getToken('paletteSemanticGreen800')({ theme });
    const commandOperatorsColor = getToken('legacyTagTextPrimary')({ theme });
    const entitiesColor = getToken('paletteSemanticBlue600')({ theme });
    const parametersColor = getToken('legacyTagTextLavender')({ theme });
    const operatorsColor = getToken('textStaticSecondary')({ theme });
    const valuesColor = getToken('legacyTagTextPrimary')({ theme });
    const valuesBackgroundColor = getToken('legacyTagBackgroundPrimary')({ theme });
    const textsColor = getToken('legacyTagTextPrimary')({ theme });
    const textsBackgroundColor = getToken('legacyTagBackgroundPrimary')({ theme });

    const suggestionsTheme: SuggestionsTheme = {
        commandsColor,
        commandOperatorsColor,
        entitiesColor,
        parametersColor,
        operatorsColor,
        valuesColor,
        valuesBackgroundColor,
        textsColor,
        textsBackgroundColor,
    };
    return toKeywordColors(suggestionsHistory, suggestionsTheme);
};

interface Style {
    color: string;
    backgroundColor?: string;
}

const toKeywordColors = (suggestions: AssistantSuggestion[], suggestionsTheme: SuggestionsTheme): KeywordColor[] => {
    const styleMap: Record<AssistantSuggestionType, Style> = {
        [AssistantSuggestionType.Command]: { color: suggestionsTheme.commandsColor },
        [AssistantSuggestionType.CommandOperator]: { color: suggestionsTheme.commandOperatorsColor },
        [AssistantSuggestionType.Entity]: { color: suggestionsTheme.entitiesColor },
        [AssistantSuggestionType.Parameter]: { color: suggestionsTheme.parametersColor },
        [AssistantSuggestionType.Operator]: { color: suggestionsTheme.operatorsColor },
        [AssistantSuggestionType.Value]: {
            color: suggestionsTheme.valuesColor,
            backgroundColor: suggestionsTheme.valuesBackgroundColor,
        },
        [AssistantSuggestionType.Text]: {
            color: suggestionsTheme.textsColor,
            backgroundColor: suggestionsTheme.textsBackgroundColor,
        },
    };

    return suggestions?.map(
        (suggestion) =>
            ({
                keyword: suggestion.text,
                color: styleMap[suggestion.type].color,
                backgroundColor: styleMap[suggestion.type].backgroundColor ?? 'transparent',
                isCommand: suggestion.type === AssistantSuggestionType.Command,
            }) as KeywordColor
    );
};
